/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import { get, join } from 'lodash';
import PropTypes from 'prop-types';

import { CartItemAlert, CartItemOptions } from 'app/common/components';
import {
  ItemAsset,
  ItemHeader
} from 'app/common/components/order-tracking/OrderReviewItem/components';
import { useProductUrl } from 'app/common/hooks';
import { isCartItemPriceUnavailable } from 'app/product/utils/PricingUtils';

import { CheckoutItemPricing } from './components';

const CheckoutItem = ({
  attributes,
  attributeChoices,
  dependentCartItems,
  imageAsset,
  sku,
  name,
  position,
  quantity = 1,
  uri,
  coreCost,
  unitPriceType,
  displayTotal,
  displayAdjustmentsTotal,
  displayUnitPrice,
  displayOriginalUnitPrice,
  discounted,
  unitPrice,
  overridePriceFlag
}) => {
  const url = useProductUrl(uri);
  const gtmItem = React.useMemo(
    () => ({
      brand: get(attributes, 'productBrand.value'),
      category: join(get(attributes, 'categoryNames', []), ', '),
      id: sku,
      name: get(attributes, 'productDescription'),
      price: get(displayUnitPrice, 'amount', 0.0),
      position,
      quantity
    }),
    [attributes, sku, displayUnitPrice, quantity, position]
  );

  return (
    <li className="flex justify-start py-2 border-b border-solid border-gray-400 last:border-b-0">
      <ItemAsset imageAsset={imageAsset} productName={name} productUrl={url} />
      <section className="flex flex-wrap flex-auto items-start justify-between basis-1/2 pr-2">
        <div className="mb-2 pr-4">
          <ItemHeader
            name={name}
            url={url}
            gtmItem={gtmItem}
            attributes={attributes}
          />
          <CartItemOptions
            attributeChoices={attributeChoices}
            dependentCartItems={dependentCartItems}
          />
        </div>
        {!!displayUnitPrice && (
          <CheckoutItemPricing
            adjustmentsTotal={displayAdjustmentsTotal}
            quantity={quantity}
            total={displayTotal}
            unitPrice={displayUnitPrice}
            coreCost={coreCost}
            isSale={discounted}
            originalPrice={displayOriginalUnitPrice}
            priceUnavailable={isCartItemPriceUnavailable({
              attributes,
              overridePriceFlag,
              unitPrice
            })}
          />
        )}
        <CartItemAlert
          unitPriceType={unitPriceType}
          unitPrice={displayUnitPrice}
          attributes={attributes}
        />
      </section>
    </li>
  );
};

CheckoutItem.propTypes = {
  name: PropTypes.string.isRequired,
  quantity: PropTypes.number,
  unitPrice: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired
  })
};

export default CheckoutItem;
export { CheckoutItem };
